import { useAuth } from '@/hooks/useAuth'
import { useMemo } from 'react'

export interface TenantConfig {
  domain: string
  name: string
  features: {
    workforce: boolean
    documents: boolean
    analytics: boolean
    payroll: boolean
    performance: boolean
  }
  branding: {
    primaryColor: string
    logo?: string
    customTheme?: boolean
  }
  limits: {
    employees: number
    departments: number
    documents: number
  }
}

const TENANT_CONFIGS: Record<string, TenantConfig> = {
  monumenta: {
    domain: 'monumenta',
    name: 'Monumenta',
    features: {
      workforce: true,
      documents: true,
      analytics: true,
      payroll: true,
      performance: true
    },
    branding: {
      primaryColor: '#D2FA52',
      customTheme: true
    },
    limits: {
      employees: 1000,
      departments: 50,
      documents: 10000
    }
  },
  demo: {
    domain: 'demo',
    name: 'Demo Company',
    features: {
      workforce: true,
      documents: false,
      analytics: true,
      payroll: false,
      performance: false
    },
    branding: {
      primaryColor: '#4760E0'
    },
    limits: {
      employees: 50,
      departments: 10,
      documents: 100
    }
  }
}

export function useTenant() {
  const { user } = useAuth()

  const tenantConfig = useMemo(() => {
    const tenantId = user?.employee?.tenantId || "default" || 'monumenta'
    return TENANT_CONFIGS[tenantId] || TENANT_CONFIGS.monumenta
  }, [user?.employee?.tenantId || "default"])

  const tenantDomain = useMemo(() => {
    return user?.employee?.tenantId || "default" || 'monumenta'
  }, [user?.employee?.tenantId || "default"])

  const hasFeature = (feature: keyof TenantConfig['features']) => {
    return tenantConfig.features[feature]
  }

  const isWithinLimits = (resource: keyof TenantConfig['limits'], current: number) => {
    return current < tenantConfig.limits[resource]
  }

  return {
    tenant: tenantConfig,
    tenantDomain,
    hasFeature,
    isWithinLimits
  }
}